import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import RelativeContainer from '../../../../../../../src/packages/kakapo-ui-kit/components/atoms/RelativeContainer';
import { PlainHorizontalCardPadding } from '../../../../../../../src/packages/kakapo-ui-kit/components/cards/PlainHorizontalCard';
import { Button } from '../../../common/button/Button';
import { FlexGap } from '../../../common/FlexGap';
import { Heading, Paragraph } from '../../../common/Text';
import { tokens } from '../../../theme/tokens';

interface IProps {
	isDragging: boolean;
	accept: Array<string>;
	inputRef: React.RefObject<HTMLInputElement>;
	translationPropertyKey: string;
	hasVideo?: boolean;
	onError?: (error: Error) => void;
	paddingSize?: PlainHorizontalCardPadding;
	onSelectFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const HiddenFullSizeInput = styled.input`
	width: 100%;
	height: 100%;
	left: 0;
	top: 0;
	position: absolute;
	opacity: 0;
`;

const Element = styled.div<{ $isDragging: boolean }>`
	border: 1px dashed ${tokens.border.default};
	border-radius: ${tokens.radius.md}px;
	position: relative;
	background: ${tokens.surface.white};
	box-sizing: border-box;

	${(props) =>
		props.$isDragging &&
		`
		border-style: solid;
		border-color: ${tokens.border.strong};
		background: ${tokens.surface.hover};
	`}
`;

const Overlay = styled.div`
	border-radius: ${tokens.radius.md}px;
	width: 100%;
	height: 100%;
	left: 0;
	top: 0;
	position: absolute;
	z-index: 10;
	display: flex;
	justify-content: center;
	align-items: center;
	pointer-events: none;
	background: ${tokens.text.primary};
`;

export function ImageUploadDropZone({
	accept,
	isDragging,
	inputRef,
	onSelectFile,
	translationPropertyKey,
	paddingSize,
	hasVideo,
}: IProps) {
	const { t } = useTranslation();
	const compact = paddingSize === PlainHorizontalCardPadding.Small;

	return (
		<Element $isDragging={isDragging}>
			<Box
				p={`${compact ? tokens.padding.container : tokens.padding.section}px`}
				textAlign="center"
			>
				<HiddenFullSizeInput
					type="file"
					accept={accept.join(', ')}
					ref={inputRef}
					id="ImageUploadDropZoneInput"
					onChange={onSelectFile}
				/>

				{isDragging && (
					<Overlay>
						<Heading color="white">{t('common.forms.file-upload.letGoToUpload')}</Heading>
					</Overlay>
				)}

				<FlexGap flexDirection="column" alignItems="center" gap={tokens.gap.group}>
					<Heading>{t('common.forms.file-upload.dropHere')}</Heading>
					<Paragraph>{t('common.or')}</Paragraph>
					<RelativeContainer sendToTop={isDragging !== true}>
						<Button
							title={t(`common.forms.file-upload.${translationPropertyKey}.browse`)}
							onClick={() => {
								inputRef.current?.click();
							}}
						/>
					</RelativeContainer>
					{!hasVideo && (
						<Paragraph>
							{t(`common.forms.file-upload.${translationPropertyKey}.supportedImages`)}
						</Paragraph>
					)}
				</FlexGap>
			</Box>
		</Element>
	);
}
